// Top navigation bar — logo, view switcher, brand switcher, search, avatar.
// Mirrors the .topnav markup from Unified_Inbox_Mockup_v2.html.

import { Logo } from '@/components/brand/Logo';
import { useAuth } from '@/auth/AuthProvider';

export type InboxView = 'standup' | 'center' | 'pipe' | 'settings';
export type BrandFilter = 'all' | 'md' | 'hk';

type TopNavProps = {
  view: InboxView;
  onViewChange: (v: InboxView) => void;
  brand: BrandFilter;
  onBrandChange: (b: BrandFilter) => void;
  onSearchClick?: () => void;
  /** Phase 6f — navigate to /analytics when clicked. Omit to hide the icon. */
  onAnalyticsClick?: () => void;
};

export function TopNav({
  view,
  onViewChange,
  brand,
  onBrandChange,
  onSearchClick,
  onAnalyticsClick,
}: TopNavProps) {
  const { user, signOut } = useAuth();
  const initials = (user?.email ?? 'K').charAt(0).toUpperCase();

  return (
    <header className="topnav">
      <div className="wrap">
        <div className="topnav-inner">
          <a href="/" className="logo-mark" aria-label="Market Dwellings">
            <Logo />
          </a>

          <nav className="viewnav" role="tablist" aria-label="View switcher">
            <button
              role="tab"
              aria-current={view === 'standup' ? 'page' : undefined}
              onClick={() => onViewChange('standup')}
            >
              Standup
            </button>
            <button
              role="tab"
              aria-current={view === 'center' ? 'page' : undefined}
              onClick={() => onViewChange('center')}
            >
              Command Center
            </button>
            <button
              role="tab"
              aria-current={view === 'pipe' ? 'page' : undefined}
              onClick={() => onViewChange('pipe')}
            >
              Pipeline
            </button>
          </nav>

          <div className="top-right">
            <div className="brand-switch" role="tablist" aria-label="Brand filter">
              <button
                role="tab"
                aria-current={brand === 'all' ? 'page' : undefined}
                onClick={() => onBrandChange('all')}
              >
                <span className="brand-dot" />
                All
              </button>
              <button
                role="tab"
                aria-current={brand === 'md' ? 'page' : undefined}
                onClick={() => onBrandChange('md')}
              >
                <span className="brand-dot md" />
                <span className="label-full">Market&nbsp;</span>MD
              </button>
              <button
                role="tab"
                aria-current={brand === 'hk' ? 'page' : undefined}
                onClick={() => onBrandChange('hk')}
              >
                <span className="brand-dot hk" />
                <span className="label-full">Harper</span>HK
              </button>
            </div>

            <button
              type="button"
              className="icon-btn"
              aria-label="Search"
              onClick={onSearchClick}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="7" />
                <path d="m20 20-3.5-3.5" strokeLinecap="round" />
              </svg>
            </button>

            {onAnalyticsClick ? (
              <button
                type="button"
                className="icon-btn"
                aria-label="Analytics"
                title="Analytics"
                onClick={onAnalyticsClick}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 3v18h18" strokeLinecap="round" />
                  <path d="M7 15l4-4 4 4 5-7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            ) : null}

            <button
              type="button"
              className="avatar"
              aria-label="Account menu — sign out"
              title={user?.email ?? 'Signed in'}
              onClick={() => void signOut()}
            >
              {initials}
            </button>
          </div>
        </div>
      </div>
    </header>
  );
}
